import React, { useState } from 'react';
import { GitMerge, Clock, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import T from 'Components/Helpers/translate';
import useAgentRunEvents from '../useAgentRunEvents';

/**
 * WEB-6800: a delegation rendered inline in the parent (delegating) conversation.
 *
 * Data comes from the parent assistant message's `metadata.toolTrace` — the
 * `delegateTask` tool result (see `extractDelegations`). The delegated work runs
 * as a headless child conversation; because that child run is owned by the same
 * user, its `agent.runStarted` / `agent.runFinalized` Pusher events reach us, and
 * (WEB-6800) carry the child `conversationId`, so the chip updates its status live
 * without ever needing the child runId up front. Clicking opens the child
 * conversation in the side drawer.
 */
export type DelegationData = {
	conversationId: number;
	agentId: number;
	agentName?: string;
	title?: string;
	rationale?: string;
	/** true = the agent delegated to itself (a background run); false = handed to another agent. */
	isSelf?: boolean;
	/** Initial status from the tool result (typically 'running'). */
	status: string;
};

/**
 * Deterministic accent colour for a background run, derived from its child
 * conversationId. The same colour is rendered as a leading dot on BOTH the run
 * chip and its result chip, so a run and its result read as a matched pair even
 * when there are several in-flight (and even for self-runs, where the agent —
 * and therefore agentColor — is identical across runs). WEB-6800.
 */
export const delegationPairColor = (conversationId: number): string => `hsl(${(conversationId * 47) % 360} 62% 52%)`;

const PairDot = ({ conversationId }: { conversationId: number }) => (
	<span className="ws-pair-dot" style={{ background: delegationPairColor(conversationId) }} aria-hidden="true" />
);

const STATUS_ICON: Record<string, React.ReactNode> = {
	running: <Clock style={{ width: 10, height: 10, flexShrink: 0, color: 'var(--ws-yellow)' }} />,
	succeeded: <CheckCircle style={{ width: 10, height: 10, flexShrink: 0, color: 'var(--ws-green)' }} />,
	failed: <XCircle style={{ width: 10, height: 10, flexShrink: 0, color: 'var(--ws-red)' }} />,
	cancelled: <XCircle style={{ width: 10, height: 10, flexShrink: 0, color: 'var(--ws-red)' }} />,
	timed_out: <XCircle style={{ width: 10, height: 10, flexShrink: 0, color: 'var(--ws-red)' }} />
};

const DelegationChip = ({
	delegation,
	onOpen
}: {
	delegation: DelegationData;
	onOpen: (delegation: DelegationData) => void;
}) => {
	const [status, setStatus] = useState<string>(delegation.status || 'running');

	useAgentRunEvents({
		onRunStarted: data => {
			if (data.conversationId === delegation.conversationId) setStatus('running');
		},
		onRunFinalized: data => {
			if (data.conversationId === delegation.conversationId) setStatus(data.status);
		}
	});

	const done = status !== 'running' && status !== 'queued';
	const agentName = delegation.agentName || T('assistant.delegation') || 'Delegated work';
	// Self-delegation = the agent offloading its own work to a background run;
	// delegation to a different agent = a hand-off. Label accordingly (click opens
	// it either way). isSelf===false → "Delegated to X"; otherwise (self, or older
	// chips without the flag) → "Background run".
	const label =
		delegation.isSelf === false
			? `${T('assistant.delegatedTo') || 'Delegated to'} ${agentName}${
					delegation.title ? `: ${delegation.title}` : ''
			  }`
			: `${T('assistant.backgroundRun') || 'Background run'}${delegation.title ? `: ${delegation.title}` : ''}`;

	return (
		<button
			type="button"
			className="ws-work-item-chip ws-work-item-chip--delegation ws-work-item-chip--clickable"
			onClick={() => onOpen(delegation)}
			title={delegation.rationale || label}
		>
			<PairDot conversationId={delegation.conversationId} />
			{STATUS_ICON[status] ?? <GitMerge style={{ width: 10, height: 10, flexShrink: 0 }} />}
			<span>{label}</span>
			{done ? (
				<ArrowRight style={{ width: 10, height: 10, flexShrink: 0 }} />
			) : (
				<GitMerge style={{ width: 10, height: 10, flexShrink: 0, opacity: 0.6 }} />
			)}
		</button>
	);
};

/**
 * Extract delegations from an assistant message's metadata.toolTrace. Each
 * `delegateTask` tool-call's `output` is the stringified DelegateTaskResult
 * `{ ok, conversationId, agentId, agentName, status, rationale }`.
 */
export const extractDelegations = (metadata: unknown): DelegationData[] => {
	const trace = (metadata as { toolTrace?: unknown } | undefined)?.toolTrace;
	if (!Array.isArray(trace)) return [];
	const out: DelegationData[] = [];
	for (const entry of trace) {
		if (!entry || (entry as { name?: string }).name !== 'delegateTask') continue;
		const raw = (entry as { output?: unknown }).output;
		let parsed: Record<string, unknown> | null = null;
		try {
			parsed = typeof raw === 'string' ? JSON.parse(raw) : (raw as Record<string, unknown>) ?? null;
		} catch {
			parsed = null;
		}
		if (!parsed || parsed.ok !== true || typeof parsed.conversationId !== 'number') continue;
		// `input` is persisted as a stringified JSON object (see agentEngine
		// toolTrace), so parse it to recover the task title for the chip label.
		const rawInput = (entry as { input?: unknown }).input;
		let parsedInput: Record<string, unknown> | null = null;
		try {
			parsedInput =
				typeof rawInput === 'string' ? JSON.parse(rawInput) : (rawInput as Record<string, unknown>) ?? null;
		} catch {
			parsedInput = null;
		}
		out.push({
			conversationId: parsed.conversationId as number,
			agentId: Number(parsed.agentId) || 0,
			agentName: typeof parsed.agentName === 'string' ? parsed.agentName : undefined,
			title: typeof parsedInput?.title === 'string' ? (parsedInput.title as string) : undefined,
			rationale: typeof parsed.rationale === 'string' ? parsed.rationale : undefined,
			isSelf: typeof parsed.isSelf === 'boolean' ? parsed.isSelf : undefined,
			status: typeof parsed.status === 'string' ? parsed.status : 'running'
		});
	}
	return out;
};

export type DelegationCompletionData = {
	childConversationId: number;
	agentId?: number;
	agentName?: string;
	/** true = the agent ran this itself in the background; false = a delegated agent did it. */
	isSelf?: boolean;
	status: string;
};

/**
 * Extract the delegated-work completion marker an assistant message carries when
 * it's the summary posted back to the PARENT conversation after a delegated run
 * finished (see agentEngine `notifyParentOfDelegatedCompletion`).
 */
export const extractDelegationCompletion = (metadata: unknown): DelegationCompletionData | null => {
	const c = (metadata as { delegationCompletion?: unknown } | undefined)?.delegationCompletion as
		| { childConversationId?: unknown; agentId?: unknown; agentName?: unknown; isSelf?: unknown; status?: unknown }
		| undefined;
	if (!c || typeof c.childConversationId !== 'number') return null;
	return {
		childConversationId: c.childConversationId,
		agentId: typeof c.agentId === 'number' ? c.agentId : undefined,
		agentName: typeof c.agentName === 'string' ? c.agentName : undefined,
		isSelf: typeof c.isSelf === 'boolean' ? c.isSelf : undefined,
		status: typeof c.status === 'string' ? c.status : 'succeeded'
	};
};

/**
 * Banner shown directly above a delegated-work *result* message in the parent
 * conversation, so it's unmistakable the summary came back from a background
 * delegated run (rather than the parent agent talking). Click opens that child
 * conversation in the drawer.
 */
export const DelegationResultHeader = ({
	completion,
	onOpen,
	compact = false
}: {
	completion: DelegationCompletionData;
	onOpen: (delegation: DelegationData) => void;
	/** Small inline variant rendered in the message header row (next to name/time). */
	compact?: boolean;
}) => {
	const failed =
		completion.status === 'failed' || completion.status === 'cancelled' || completion.status === 'timed_out';
	const open = () =>
		onOpen({
			conversationId: completion.childConversationId,
			agentId: completion.agentId ?? 0,
			agentName: completion.agentName,
			status: completion.status
		});
	const icon = failed ? (
		<XCircle style={{ width: 12, height: 12, flexShrink: 0, color: 'var(--ws-red)' }} />
	) : (
		<CheckCircle style={{ width: 12, height: 12, flexShrink: 0, color: 'var(--ws-green)' }} />
	);
	// isSelf===false → a different agent did the work ("Delegation result"); else
	// (self background run, or older messages without the flag) → "Background result".
	const isDelegated = completion.isSelf === false;
	const shortLabel = isDelegated
		? T('assistant.delegationResultShortDelegated') || 'Delegation result'
		: T('assistant.delegationResultShort') || 'Background result';

	if (compact) {
		return (
			<button
				type="button"
				className="ws-delegation-result-tag"
				title={T('assistant.delegationResultOpen') || 'Open the delegated conversation'}
				onClick={open}
			>
				<PairDot conversationId={completion.childConversationId} />
				{icon}
				<span>{shortLabel}</span>
			</button>
		);
	}

	return (
		<button
			type="button"
			className="ws-delegation-result-header"
			title={T('assistant.delegationResultOpen') || 'Open the delegated conversation'}
			onClick={open}
		>
			<PairDot conversationId={completion.childConversationId} />
			{icon}
			<span className="ws-delegation-result-label">
				{isDelegated ? T('assistant.delegationResultFrom') || 'Result from delegated work' : shortLabel}
			</span>
			{isDelegated && completion.agentName && (
				<>
					<span className="ws-delegation-result-dot">{'·'}</span>
					<span className="ws-delegation-result-agent">{completion.agentName}</span>
				</>
			)}
			<ArrowRight style={{ width: 13, height: 13, flexShrink: 0, marginLeft: 'auto' }} />
		</button>
	);
};

export default DelegationChip;
